// Minimal API service: centralizes token refresh + authorized fetch

import { formatPlainSummary, decodeHtml } from "../utils";

const JWT_REFRESH_URL = process.env.REACT_APP_JWT_REFRESH_URL;
const JWT_API_KEY = process.env.REACT_APP_JWT_API_KEY;
// const TRANSCRIPT_API_URL = process.env.REACT_APP_TRANSCRIPT_API_URL;
const SUMMARY_API_URL = process.env.REACT_APP_SUMMARY_API_URL;
const CASE_INFO_API_URL = process.env.REACT_APP_CASE_INFO_API_URL;
const CREATE_JOURNAL_URL = process.env.REACT_APP_CREATE_JOURNAL_URL;
const STORE_JOURNAL_URL = process.env.REACT_APP_STORE_JOURNAL_URL;
const DIFFERENCE_API_URL = process.env.REACT_APP_DIFFERENCE_API_URL;
const CASE_NAMES_API_URL = process.env.REACT_APP_CASE_NAME_API_URL;
const NOTES_API_URL = process.env.REACT_APP_NOTES_API_URL;

let accessTokenRef = { current: "" };
let refreshTokenRef = { current: "" };

export function initApiService({ accessRef, refreshRef }) {
  if (accessRef) accessTokenRef = accessRef;
  if (refreshRef) refreshTokenRef = refreshRef;
}

export async function refreshAccessToken() {
  const rt = refreshTokenRef.current;
  if (!rt) {
    return null;
  }
  const url = `${JWT_REFRESH_URL}?RefreshToken=${encodeURIComponent(rt)}`;

  try {
    const res = await fetch(url, {
      method: "GET",
      headers: { "x-api-key": JWT_API_KEY },
    });

    let data = {};
    try {
      data = await res.json();
    } catch (e) {
      // ignore
    }

    if (!res.ok) {
      return null;
    }

    const newToken = data.accessToken || data.AccessToken || data.id_token || data.token;
    if (!newToken) {
      return null;
    }

    if (accessTokenRef) accessTokenRef.current = newToken;
    return newToken;
  } catch (err) {
    return null;
  }
}

export async function authorizedFetch(url, options = {}) {
  const makeRequest = async (tokenForRequest) => {
    const baseHeaders = options.headers || {};
    const headers = { ...baseHeaders };
    if (tokenForRequest) headers["Authorization"] = `Bearer ${tokenForRequest}`;
    const finalOptions = { ...options, headers };
    return await fetch(url, finalOptions);
  };

  const currAccess = accessTokenRef.current;
  const currRefresh = refreshTokenRef.current;

  let response = await makeRequest(currAccess);

  if (response.status !== 401 && response.status !== 403) {
    return response;
  }

  if (!currRefresh) {
    return response;
  }

  const newToken = await refreshAccessToken();
  if (!newToken) {
    return response;
  }

  // Retry once with new token
  const retryResponse = await makeRequest(newToken);
  return retryResponse;
}

// GET CASE NAMES — send array of case numbers, receive cases with persons
export async function getCaseNamesApi(caseNumbers, obEnv) {
  if (!caseNumbers || !caseNumbers.length) return null;

  const res = await authorizedFetch(CASE_NAMES_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-OB-Environment": obEnv,
    },
    body: JSON.stringify({ caseNumbers }),
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "<no body>");
    throw new Error(`getCaseNamesApi failed: ${res.status} ${txt}`);
  }

  const data = await res.json();
  return data; // { cases: [{ caseNumber, persons: [{ casePersonName }] }] }
}

// GET SUMMARY (returns parsed JSON or throws)
// caseNamesPayload is optional — passed through to the summary API when available
export async function getSummary(contactId, caseNamesPayload = null) {
  if (!contactId) throw new Error("No contactId supplied");

  const body = { ContactId: contactId };
  if (caseNamesPayload) {
    body.caseNames = caseNamesPayload;
  }

  const res = await authorizedFetch(SUMMARY_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`getSummary failed: ${res.status} ${txt}`);
  }
  const data = await res.json();
  return data;
}

// Fetch summary and return formatted HTML (decoded)
// caseNamesPayload is forwarded to the summary API when provided
export async function getFormattedSummary(contactId, caseNamesPayload = null) {
  const data = await getSummary(contactId, caseNamesPayload);
  const error = (data.Error || "").trim();
  const formattedSummary = formatPlainSummary(data.Summary || "");
  return { html: decodeHtml(formattedSummary), error };
}

// GET CASE INFO
export async function getCaseInfo(caseNumber, obEnv) {
  if (!caseNumber) throw new Error("No caseNumber supplied");

  const res = await authorizedFetch(CASE_INFO_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-OB-Environment": obEnv,
    },
    body: JSON.stringify({ caseNumber }),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`getCaseInfo failed: ${res.status} ${txt}`);
  }
  const data = await res.json();
  const caseName = data.caseName || data.CaseName || data.case_name || null;
  return { caseName, raw: data };
}

// CREATE JOURNAL (returns parsed JSON)
export async function createJournalApi({
  caseNumber,
  shortDescription,
  longDescription,
  contactId,
  agentExtension = "N/A",
  staffId = "N/A",
  obEnv,
  originalSummary = null,
  summaryDiff = null }) {

  const additionalData = {
    originalSummary,
    summaryDiff,
  };

  const payload = {
    caseNumber,
    shortDescription,
    longDescription,
    contactId,
    agentExtension,
    initiator: "CCAA SNAP Interview",
    staffId,
    additionalData
  };

  const res = await authorizedFetch(CREATE_JOURNAL_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-OB-Environment": obEnv,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const bodyText = await res.text().catch(() => "<no body>");
    throw new Error(`createJournal failed: ${res.status} ${bodyText}`);
  }
  const data = await res.json();
  return data;
}

// NEW: fetch additional data (originalSummary / summaryDiff) for a contact
export async function fetchAdditionalData(contactId) {
  if (!contactId) return null;
  try {
    const res = await authorizedFetch(DIFFERENCE_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ContactId: contactId }),
    });

    // read raw text to handle multiple shapes
    const rawText = await res.text().catch(() => "");

    if (!res.ok) {
      return null;
    }

    // try to parse JSON (if any)
    let data = null;
    try {
      data = rawText ? JSON.parse(rawText) : null;
    } catch (e) {
      return null;
    }

    // Normalize various possible shapes:
    // 1) { additionalData: { originalSummary, summaryDiff } }
    if (data?.additionalData && (data.additionalData.originalSummary || data.additionalData.summaryDiff)) {
      return {
        originalSummary: data.additionalData.originalSummary ?? null,
        summaryDiff: data.additionalData.summaryDiff ?? null,
      };
    }

    // 2) top-level { originalSummary, summaryDiff }
    if (data?.originalSummary || data?.summaryDiff) {
      return {
        originalSummary: data.originalSummary ?? null,
        summaryDiff: data.summaryDiff ?? null,
      };
    }

    // 3) nested in body (e.g., { body: '{"additionalData": {...}}' } )
    if (data?.body) {
      let bodyObj = data.body;
      if (typeof bodyObj === "string") {
        try {
          bodyObj = JSON.parse(bodyObj);
        } catch (e) {
          // ignore parse error
        }
      }
      if (bodyObj?.additionalData && (bodyObj.additionalData.originalSummary || bodyObj.additionalData.summaryDiff)) {
        return {
          originalSummary: bodyObj.additionalData.originalSummary ?? null,
          summaryDiff: bodyObj.additionalData.summaryDiff ?? null,
        };
      }
      if (bodyObj?.originalSummary || bodyObj?.summaryDiff) {
        return {
          originalSummary: bodyObj.originalSummary ?? null,
          summaryDiff: bodyObj.summaryDiff ?? null,
        };
      }
    }

    return null;
  } catch (err) {
    return null;
  }
}

// SAVE JOURNAL DETAILS / STORE CALL DETAILS
export async function saveJournalDetailsApi({
  caseNumber,
  shortDescription,
  longDescription,
  contactId,
  journalId,
  originalSummary = null,
  summaryDiff = null,
  obEnv,
  isSummaryEdited = false,
}) {
  // normalize summaryDiff like createJournalApi
  let normalizedSummaryDiff = summaryDiff;
  if (typeof summaryDiff === "string" && summaryDiff.trim().startsWith("{")) {
    try {
      normalizedSummaryDiff = JSON.parse(summaryDiff);
    } catch (e) {
      normalizedSummaryDiff = summaryDiff;
    }
  }

  const additionalData =
    originalSummary != null || normalizedSummaryDiff != null
      ? {
        ...(originalSummary != null ? { originalSummary } : {}),
        ...(normalizedSummaryDiff != null ? { summaryDiff: normalizedSummaryDiff } : {}),
      }
      : null;

  const payload = {
    caseNumber,
    initiator: "CCAA SNAP Interview",
    shortDescription,
    longDescription,
    contactId,
    journalId,
    obEnv,
    isSummaryEdited,
    ...(additionalData ? { additionalData } : {}),
  };

  const res = await authorizedFetch(STORE_JOURNAL_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "<no body>");
    throw new Error(`saveJournalDetails failed: ${res.status} ${txt}`);
  }
  const data = await res.json();
  return data;
}

// NEW: create journal + save details flow (returns { journalId })
export async function createAndSaveJournal({
  caseNumber,
  shortDescription,
  longDescription,
  contactId,
  agentExtension = "N/A",
  staffId = "N/A",
  obEnv,
}) {
  // 1) create journal (may return journalId)
  let journalId = null;
  // We'll attempt to fetch additional data once here and reuse for save step.
  // fetch additional data once
  let originalSummary = null;
  let summaryDiff = null;
  try {
    const additional = await fetchAdditionalData(contactId);
    if (additional) {
      originalSummary = additional.originalSummary ?? null;
      summaryDiff = additional.summaryDiff ?? null;
    }
  } catch (err) {
    // proceed without additional data
  }

  const res = await createJournalApi({
    caseNumber,
    shortDescription,
    longDescription,
    contactId,
    agentExtension,
    staffId,
    obEnv,
    originalSummary,
    summaryDiff,
  });
  journalId = res?.journalId || res?.JournalId || null;

  if (!journalId) {
    throw new Error("No journal ID received from server");
  }

  // 2) save journal details
  try {
    // reuse originalSummary/summaryDiff fetched earlier when possible
    await saveJournalDetailsApi({
      caseNumber,
      shortDescription,
      longDescription,
      contactId,
      journalId,
      originalSummary,
      summaryDiff,
      obEnv,
    });
  } catch (err) {
    // ignore save failure — journal was already created
  }

  return { journalId };
}

// SAVE AGENT NOTES
export async function saveNotesApi(contactId, notes) {
  if (!contactId) throw new Error("No contactId supplied");

  const res = await authorizedFetch(NOTES_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ContactId: contactId, notes }),
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "<no body>");
    throw new Error(`saveNotes failed: ${res.status} ${txt}`);
  }
  return res;
}

// AUTO SAVE EDITED SUMMARY (convenience wrapper)
export async function autoSaveEditedSummaryApi({ initialCaseNumber = "", initialCaseName = "", longDescription = "", contactId = "", journalId = "" }) {
  return saveJournalDetailsApi({
    caseNumber: initialCaseNumber,
    caseName: initialCaseName,
    shortDescription: "Summary Updated",
    longDescription,
    contactId,
    journalId,
    isSummaryEdited: true,
  });
}

// Provide a safe getter for the current access token
export function getCurrentAccessToken() {
  return accessTokenRef && accessTokenRef.current ? accessTokenRef.current : null;
}
